import dayjs from 'dayjs';
import 'dayjs/locale/es';

dayjs.locale('es');

const ROUTE_DAY_LOCALE = 'es-UY';

const ISO_DATE_ONLY = /^(\d{4})-(\d{2})-(\d{2})$/;

function formatUtcCalendarDayParts(
  year: number,
  monthZeroBased: number,
  day: number
): string {
  const noonUtc = new Date(Date.UTC(year, monthZeroBased, day, 12, 0, 0));
  return new Intl.DateTimeFormat(ROUTE_DAY_LOCALE, {
    timeZone: 'UTC',
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
  }).format(noonUtc);
}

function formatScheduledCalendarDateLabel(value: string): string | null {
  const trimmed = value.trim();
  if (trimmed === '') {
    return null;
  }
  const match = ISO_DATE_ONLY.exec(trimmed);
  if (match) {
    const y = Number(match[1]);
    const mo = Number(match[2]);
    const d = Number(match[3]);
    if (
      !Number.isFinite(y) ||
      !Number.isFinite(mo) ||
      !Number.isFinite(d)
    ) {
      return null;
    }
    return formatUtcCalendarDayParts(y, mo - 1, d);
  }
  const parsed = dayjs(trimmed);
  if (!parsed.isValid()) {
    return null;
  }
  return parsed.format('DD/MM/YYYY');
}

function formatIsoInstantAsUtcCalendarDate(isoScheduled: string): string {
  const trimmed = isoScheduled.trim();
  if (trimmed === '') {
    return '--/--/----';
  }
  const instant = new Date(trimmed);
  if (Number.isNaN(instant.getTime())) {
    return '--/--/----';
  }
  return new Intl.DateTimeFormat(ROUTE_DAY_LOCALE, {
    timeZone: 'UTC',
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
  }).format(instant);
}

export function formatRouteScheduledCalendarDay(params: {
  scheduled: string;
  scheduledCalendarDate?: string | null;
}): string {
  const fromApi = params.scheduledCalendarDate;
  if (fromApi != null) {
    const formatted = formatScheduledCalendarDateLabel(String(fromApi));
    if (formatted !== null) {
      return formatted;
    }
  }
  return formatIsoInstantAsUtcCalendarDate(params.scheduled);
}

export const formatDateTime = (dateString: string): string => {
  return dayjs(dateString).format('DD/MM/YYYY HH:mm');
};

export const formatTime = (dateString: string): string => {
  return dayjs(dateString).format('HH:mm');
};

export function formatVisitCardTime(
  visitedAt: string | null | undefined,
  scheduledFrom: string | null | undefined
): string {
  const toHourMinute = (value: string | null | undefined): string | null => {
    if (value == null) return null;
    const trimmed = String(value).trim();
    if (trimmed === '') return null;
    const parsed = dayjs(trimmed);
    if (!parsed.isValid()) return null;
    return parsed.format('HH:mm');
  };

  const visitTime = toHourMinute(visitedAt);
  if (visitTime !== null) return visitTime;

  const scheduledTime = toHourMinute(scheduledFrom);
  if (scheduledTime !== null) return scheduledTime;

  return '--:--';
}

export const formatDate = (dateString: string): string => {
  return dayjs(dateString).format('DD/MM/YYYY');
};

export const formatDuration = (totalSeconds: number): string => {
  const hours = Math.floor(totalSeconds / 3600);
  const minutes = Math.floor((totalSeconds % 3600) / 60);
  return `${hours.toString().padStart(2, '0')}:${minutes.toString().padStart(2, '0')}`;
};
